import React, { useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { emailConfigApi, type EmailLog, type EmailStats, type PaginatedResponse } from 'lib/api'
import { Card, CardContent, CardHeader, CardTitle } from 'components/ui/Card'
import { Input } from 'components/ui/Input'
import { Button } from 'components/ui/Button'
import { Badge } from 'components/ui/Badge'
import { RefreshCw, Search, Mail, AlertCircle, CheckCircle2, BarChart3 } from 'lucide-react'

const STATUS_VARIANT: Record<EmailLog['status'], 'default' | 'secondary' | 'destructive' | 'outline'> = {
  sent: 'default',
  delivered: 'secondary',
  opened: 'secondary',
  failed: 'destructive',
}

const EMPTY_STATS: EmailStats = {
  total_sent: 0,
  total_delivered: 0,
  total_failed: 0,
  delivery_rate: 0,
}

export function OutgoingEmailLogTable() {
  const { t } = useTranslation()
  const [logs, setLogs] = useState<EmailLog[]>([])
  const [stats, setStats] = useState<EmailStats>(EMPTY_STATS)
  const [loading, setLoading] = useState(false)
  const [search, setSearch] = useState('')
  const [statusFilter, setStatusFilter] = useState('')
  const [recipientTypeFilter, setRecipientTypeFilter] = useState('')
  const [page, setPage] = useState(1)
  const [totalCount, setTotalCount] = useState(0)
  const pageSize = 20

  const loadData = useCallback(async () => {
    setLoading(true)
    try {
      const [logsResponse, statsResponse] = await Promise.all([
        emailConfigApi.getLogs({
          page,
          page_size: pageSize,
          status: statusFilter || undefined,
          recipient_type: recipientTypeFilter === 'candidate' || recipientTypeFilter === 'system' ? recipientTypeFilter : undefined,
          search: search.trim() || undefined,
        }),
        emailConfigApi.getStats(),
      ])

      const data = logsResponse.data as PaginatedResponse<EmailLog> | EmailLog[]
      if (data && typeof data === 'object' && 'results' in data) {
        setLogs(data.results || [])
        setTotalCount(data.count || 0)
      } else {
        const list = Array.isArray(data) ? data : []
        setLogs(list)
        setTotalCount(list.length)
      }

      setStats(statsResponse.data)
    } catch (error) {
      console.error('Error loading email trace data:', error)
      setLogs([])
      setTotalCount(0)
      setStats(EMPTY_STATS)
    } finally {
      setLoading(false)
    }
  }, [page, statusFilter, recipientTypeFilter, search])

  useEffect(() => {
    loadData()
  }, [loadData])

  const totalPages = Math.max(1, Math.ceil(totalCount / pageSize))

  const formatDate = (value: string) => {
    try {
      return new Date(value).toLocaleString()
    } catch {
      return value
    }
  }

  return (
    <Card>
      <CardHeader className="max-[1149px]:p-4">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <CardTitle className="flex items-center space-x-2 text-foreground max-[1149px]:text-base">
            <BarChart3 className="h-5 w-5" />
            <span>{t('settings.emailConfig.emailStatsTitle', 'Sending Statistics')}</span>
          </CardTitle>
          <Button variant="outline" size="sm" onClick={loadData} disabled={loading} className="shrink-0 self-start sm:self-auto">
            <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            {t('common.refresh', 'Refresh')}
          </Button>
        </div>
      </CardHeader>

      <CardContent className="space-y-6 max-[1149px]:p-4">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 max-[1149px]:grid-cols-2 max-[1149px]:gap-3">
          <div className="text-center p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
            <div className="text-2xl font-bold text-blue-600 dark:text-blue-400">{stats.total_sent}</div>
            <div className="text-sm text-muted-foreground">{t('settings.emailConfig.emailsSent', 'Emails sent')}</div>
          </div>
          <div className="text-center p-4 bg-green-50 dark:bg-green-900/20 rounded-lg">
            <div className="text-2xl font-bold text-green-600 dark:text-green-400">{stats.total_delivered}</div>
            <div className="text-sm text-muted-foreground">{t('settings.emailConfig.delivered', 'Delivered')}</div>
          </div>
          <div className="text-center p-4 bg-red-50 dark:bg-red-900/20 rounded-lg">
            <div className="text-2xl font-bold text-red-600 dark:text-red-400">{stats.total_failed}</div>
            <div className="text-sm text-muted-foreground">{t('settings.emailConfig.failed', 'Failed')}</div>
          </div>
        </div>

        <div className="p-3 bg-muted rounded-lg">
          <div className="flex items-center justify-between text-sm">
            <span className="text-muted-foreground">{t('settings.emailConfig.deliveryRate', 'Delivery rate:')}</span>
            <span className="font-medium text-green-600 dark:text-green-400">{stats.delivery_rate}%</span>
          </div>
        </div>

        <div className="border-t pt-6 space-y-4">
          <h3 className="text-sm font-semibold text-foreground">
            {t('settings.emailConfig.logTitle', 'Outgoing Email Trace')}
          </h3>

          <div className="flex flex-col sm:flex-row gap-3">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                value={search}
                onChange={(e) => {
                  setSearch(e.target.value)
                  setPage(1)
                }}
                placeholder={t('settings.emailConfig.logSearchPlaceholder', 'Search by recipient or subject...')}
                className="pl-9"
              />
            </div>
            <select
              value={recipientTypeFilter}
              onChange={(e) => {
                setRecipientTypeFilter(e.target.value)
                setPage(1)
              }}
              className="h-10 rounded-md border border-input bg-background px-3 text-sm min-w-[160px]"
            >
              <option value="">{t('settings.emailConfig.logAllTypes', 'All emails')}</option>
              <option value="candidate">{t('settings.emailConfig.logCandidateEmails', 'Candidate emails')}</option>
              <option value="system">{t('settings.emailConfig.logSystemEmails', 'System emails')}</option>
            </select>
            <select
              value={statusFilter}
              onChange={(e) => {
                setStatusFilter(e.target.value)
                setPage(1)
              }}
              className="h-10 rounded-md border border-input bg-background px-3 text-sm min-w-[160px]"
            >
              <option value="">{t('settings.emailConfig.logAllStatuses', 'All statuses')}</option>
              <option value="sent">{t('settings.emailConfig.emailsSent', 'Emails sent')}</option>
              <option value="failed">{t('settings.emailConfig.failed', 'Failed')}</option>
              <option value="delivered">{t('settings.emailConfig.delivered', 'Delivered')}</option>
            </select>
          </div>

          {logs.length === 0 && !loading ? (
            <div className="text-center py-10 text-muted-foreground rounded-lg border border-dashed">
              <Mail className="h-10 w-10 mx-auto mb-3 opacity-40" />
              <p className="text-sm">{t('settings.emailConfig.noLogs', 'No outgoing emails in the last 30 days')}</p>
            </div>
          ) : (
            <div className="overflow-x-auto rounded-lg border">
              <table className="w-full text-sm">
                <thead className="bg-muted/50">
                  <tr>
                    <th className="text-left px-4 py-3 font-medium">{t('settings.emailConfig.logRecipient', 'Recipient')}</th>
                    <th className="text-left px-4 py-3 font-medium">{t('settings.emailConfig.logSubject', 'Subject')}</th>
                    <th className="text-left px-4 py-3 font-medium">{t('settings.emailConfig.logType', 'Type')}</th>
                    <th className="text-left px-4 py-3 font-medium">{t('settings.emailConfig.logStatus', 'Status')}</th>
                    <th className="text-left px-4 py-3 font-medium hidden md:table-cell">{t('settings.emailConfig.logSender', 'Sent by')}</th>
                    <th className="text-left px-4 py-3 font-medium">{t('settings.emailConfig.logDate', 'Date')}</th>
                  </tr>
                </thead>
                <tbody>
                  {loading && logs.length === 0 ? (
                    <tr>
                      <td colSpan={6} className="px-4 py-8 text-center text-muted-foreground">
                        <RefreshCw className="h-5 w-5 animate-spin mx-auto mb-2" />
                        {t('common.loading', 'Loading...')}
                      </td>
                    </tr>
                  ) : (
                    logs.map((log) => (
                      <tr key={log.id} className="border-t hover:bg-muted/30">
                        <td className="px-4 py-3 whitespace-nowrap">{log.to_email}</td>
                        <td className="px-4 py-3 max-w-[200px] truncate" title={log.subject}>{log.subject}</td>
                        <td className="px-4 py-3">
                          <Badge variant="outline">
                            {log.candidate_id
                              ? t('settings.emailConfig.logTypeCandidate', 'Candidate')
                              : t('settings.emailConfig.logTypeSystem', 'System')}
                          </Badge>
                        </td>
                        <td className="px-4 py-3">
                          <Badge variant={STATUS_VARIANT[log.status] || 'outline'} className="capitalize">
                            {log.status === 'failed' ? (
                              <AlertCircle className="h-3 w-3 mr-1" />
                            ) : (
                              <CheckCircle2 className="h-3 w-3 mr-1" />
                            )}
                            {log.status}
                          </Badge>
                          {log.error_message && (
                            <p className="text-xs text-destructive mt-1 max-w-[180px] truncate" title={log.error_message}>
                              {log.error_message}
                            </p>
                          )}
                        </td>
                        <td className="px-4 py-3 hidden md:table-cell text-muted-foreground">
                          {log.sent_by_name || '—'}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-muted-foreground">
                          {formatDate(log.sent_at)}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          )}

          {totalCount > pageSize && (
            <div className="flex items-center justify-between text-sm text-muted-foreground">
              <span>
                {t('settings.emailConfig.logShowing', {
                  from: (page - 1) * pageSize + 1,
                  to: Math.min(page * pageSize, totalCount),
                  total: totalCount,
                  defaultValue: `Showing ${(page - 1) * pageSize + 1}-${Math.min(page * pageSize, totalCount)} of ${totalCount}`,
                })}
              </span>
              <div className="flex gap-2">
                <Button variant="outline" size="sm" disabled={page <= 1 || loading} onClick={() => setPage((p) => p - 1)}>
                  {t('common.previous', 'Previous')}
                </Button>
                <Button variant="outline" size="sm" disabled={page >= totalPages || loading} onClick={() => setPage((p) => p + 1)}>
                  {t('common.next', 'Next')}
                </Button>
              </div>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  )
}
